import type { FormKitSchemaFormKit, FormKitSchemaDOMNode } from '@formkit/core'
import type {
  WebformElementFragment,
  WebformItemFragment,
} from '#build/graphql-operations'

type ItemWithTypedElement<T> = WebformItemFragment & { element: T }

type MappedWebformElement =
  FormKitSchemaFormKit | FormKitSchemaDOMNode | null | undefined

type WebformMapperContext = {
  buildChildren: (item: WebformItemFragment) => MappedWebformElement[]
}

type ExtractFragment<T extends WebformElementFragment['__typename'][]> =
  WebformElementFragment & { __typename: T[number] }

type WebformItemElementTypename = WebformElementFragment['__typename']

export interface WebformElementMapper<
  T extends WebformElementFragment['__typename'],
> {
  types: T[]
  applies: (element: WebformElementFragment) => element is ExtractFragment<T[]>
  build: (
    item: ItemWithTypedElement<ExtractFragment<T[]>>,
    ctx: WebformMapperContext,
  ) => MappedWebformElement
}

function isOfType<T extends WebformElementFragment['__typename'][]>(
  element: WebformElementFragment,
  types: T,
): element is ExtractFragment<T> {
  return types.includes(element.__typename)
}

/**
 * Defines a mapper for one or more webform element GraphQL types.
 *
 * The mapper is expected to return the mapped FormKit schema item.
 */
export function defineWebformElementMapper<
  T extends WebformItemElementTypename,
>(
  types: T[],
  build: (
    item: ItemWithTypedElement<ExtractFragment<T[]>>,
    ctx: WebformMapperContext,
  ) => MappedWebformElement,
): WebformElementMapper<T> {
  return {
    types,
    applies: (element): element is ExtractFragment<T[]> =>
      isOfType(element, types),
    build,
  }
}
